/**
 * Field-level diff between the offline edit sitting in the queue and
 * the version currently on the server — and the merge that lets the
 * user keep some of each.
 *
 * `mine` is the queued request body, `server` the snapshot the 409
 * carried back, `labels` the human names the backend resolved from the
 * document's template:
 *
 *   { fields: {key: Label},            // top-level scalars
 *     maps:   {field: {key: Label}},   // dict / row-collection fields
 *     header: {key: Label},            // checklist header fields
 *     sections: [{title, items: […]}] } // checklist sections
 *
 * Rows come back with a machine-readable `path` so a merged body can be
 * rebuilt from the user's per-field choices.
 */

const MAX_ROWS = 200;
const EMPTY = '—';

// Server bookkeeping — never worth showing as a "difference".
const SKIP_KEYS = new Set([
  'id', 'created_at', 'created_by', 'created_by_name', 'updated_at', 'updated_by',
  'base_updated_at', 'force', 'project_id', 'project_name', 'template_id',
  '_offline_created', '_createPayload',
]);

const STATUS_LABEL = {
  draft: 'Draft', in_progress: 'In progress', completed: 'Completed',
  conditional_approval: 'Conditionally approved', signed_off: 'Signed off',
  not_started: 'Not started', blocked: 'Blocked',
};

const isPlain = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

const norm = (v) => {
  if (v === undefined || v === null || v === '') return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

export const prettyKey = (k) => String(k)
  .replace(/__+/g, ' / ')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

const show = (path, v) => {
  const s = norm(v);
  if (s === '') return EMPTY;
  const last = path[path.length - 1];
  if (last === 'status' || last === 'repair_status') return STATUS_LABEL[s] || s;
  if (/^data:image\//.test(s)) return 'a signature image';
  return s.length > 140 ? `${s.slice(0, 140)}…` : s;
};

/** Human label for one diff path, using whatever the backend supplied. */
export function makeLabelFor(labels = {}) {
  const { fields = {}, maps = {}, header = {}, sections = [] } = labels || {};
  return (path) => {
    const [head, ...rest] = path;
    if (rest.length === 0) return fields[head] || prettyKey(head);

    // Checklist sections: sections_data[s].items[i].<field>
    if (head === 'sections_data' && sections.length) {
      const sec = sections[path[1]] || {};
      const title = sec.title || `Section ${Number(path[1]) + 1}`;
      if (path[2] === 'items') {
        const item = sec.items?.[path[3]] || `Item ${Number(path[3]) + 1}`;
        const tail = path.slice(4).map(prettyKey).join(' ');
        return `${title} — ${item}${tail ? ` (${tail})` : ''}`;
      }
      return `${title} — ${path.slice(2).map(prettyKey).join(' ')}`;
    }

    // Checklist header fields
    if (head === 'header_data') return header[path[1]] || prettyKey(path[1]);

    // Anything the backend gave us a key→label map for.
    const map = maps[head];
    if (map) {
      if (typeof path[1] === 'number') {
        const cell = map[path[2]] || prettyKey(path[2]);
        return `Row ${path[1] + 1} — ${cell}`;
      }
      return map[path[1]] || prettyKey(path[1]);
    }

    const base = fields[head] || prettyKey(head);
    return `${base} — ${path.slice(1).map((p) => (typeof p === 'number' ? `#${p + 1}` : prettyKey(p))).join(' ')}`;
  };
}

// Arrays of records are aligned on an identity field where one exists,
// so a reordered list doesn't read as "everything changed".
const IDENTITY_KEYS = ['item_index', 'section_index', 'row_id', 'id', 'key', 'role'];

const identityOf = (arr) => {
  const first = (arr || []).find(isPlain);
  if (!first) return null;
  return IDENTITY_KEYS.find((k) => first[k] !== undefined) || null;
};

function walk(a, b, path, labelPath, rows, labelFor) {
  if (rows.length >= MAX_ROWS) return;
  if (norm(a) === norm(b)) return;

  if (isPlain(a) || isPlain(b)) {
    const keys = Array.from(new Set([...Object.keys(a || {}), ...Object.keys(b || {})]));
    keys.forEach((k) => {
      if (SKIP_KEYS.has(k)) return;
      walk(a?.[k], b?.[k], [...path, k], [...labelPath, k], rows, labelFor);
    });
    return;
  }

  if (Array.isArray(a) || Array.isArray(b)) {
    const A = Array.isArray(a) ? a : [];
    const B = Array.isArray(b) ? b : [];
    // Arrays of plain values read better as a single line.
    if (![...A, ...B].some(isPlain)) {
      rows.push({
        path, label: labelFor(labelPath), mine: show(path, a), theirs: show(path, b),
        mineRaw: a ?? null, theirsRaw: b ?? null,
      });
      return;
    }
    const idKey = identityOf(A) || identityOf(B);
    if (idKey) {
      const seen = new Set();
      A.forEach((item, i) => {
        const idv = item?.[idKey];
        seen.add(norm(idv));
        const mate = B.find((x) => norm(x?.[idKey]) === norm(idv));
        const labelIdx = typeof idv === 'number' ? idv : i;
        walk(item, mate, [...path, i], [...labelPath, labelIdx], rows, labelFor);
      });
      B.forEach((item) => {
        const idv = item?.[idKey];
        if (seen.has(norm(idv))) return;
        const labelIdx = typeof idv === 'number' ? idv : A.length;
        walk(undefined, item, [...path, A.length], [...labelPath, labelIdx], rows, labelFor);
      });
      return;
    }
    const n = Math.max(A.length, B.length);
    for (let i = 0; i < n; i += 1) {
      walk(A[i], B[i], [...path, i], [...labelPath, i], rows, labelFor);
    }
    return;
  }

  rows.push({
    path, label: labelFor(labelPath), mine: show(path, a), theirs: show(path, b),
    mineRaw: a ?? null, theirsRaw: b ?? null,
  });
}

export function computeConflictDiff(mine, server, labels) {
  if (!mine || !server) return [];
  const rows = [];
  const labelFor = makeLabelFor(labels);
  const keys = Array.from(new Set([...Object.keys(mine), ...Object.keys(server)]));
  keys.forEach((k) => {
    if (SKIP_KEYS.has(k)) return;
    // Only compare what the client actually sent — a field the offline
    // save left out isn't a difference of opinion.
    if (!(k in mine)) return;
    walk(mine[k], server[k], [k], [k], rows, labelFor);
  });
  return rows;
}

function setPath(target, path, value) {
  let node = target;
  for (let i = 0; i < path.length - 1; i += 1) {
    const key = path[i];
    const nextIsIndex = typeof path[i + 1] === 'number';
    if (node[key] === undefined || node[key] === null || typeof node[key] !== 'object') {
      node[key] = nextIsIndex ? [] : {};
    }
    node = node[key];
  }
  node[path[path.length - 1]] = value;
}

/**
 * Rebuild the request body from the user's per-field choices.
 * `selections[i] === 'theirs'` takes the server's value for that row.
 */
export function mergeConflictBody(mine, rows, selections) {
  const out = JSON.parse(JSON.stringify(mine || {}));
  (rows || []).forEach((r, i) => {
    if (selections?.[i] !== 'theirs') return;
    setPath(out, r.path, r.theirsRaw);
  });
  return out;
}

export default computeConflictDiff;
